import Moment from 'moment'
import * as Redux from 'redux'
import * as IsActive from '../../components/is_active'
import * as Modal from '../../components/modal'
import * as Sortable from '../../components/sortable'
import * as Request from '../../request'
import * as Actions from './actions'
import * as Constants from './constants'
import * as Types from './types'

const modalReducerEvent: Modal.ReducerEvent<Types.ModalData> = {
  initializeState: () => Constants.emptyAddUserModalState,
  requestEvent: Constants.ADD_USER,
}

const normalizeTypes: Sortable.TypeNormalizer<Types.User> =
  (payload: Sortable.SortableData<Types.User>): Sortable.SortableData<Types.User> => {
  return payload.map((lp: Types.User) => ({
    ...lp,
    UserCreateDate_datetime: Moment(lp.UserCreateDate),
    UserLastModifiedDate_datetime: Moment(lp.UserLastModifiedDate),
  }))
}

const reducerEvent: Sortable.ReducerEvent<Types.User> = {
  defaultSort: { field: 'email', dir: 'asc' },
  normalizer: normalizeTypes,
  requestEvent: Constants.FETCH_USERS,
}

type UsersModalReducer = Redux.Reducer<Types.UserModalState>

interface IndexedRow extends Types.PersonRole {
  rowIndex: number
}

const rowIndexById = (state: Types.UserModalState, id: string | number): number => {
  return state.data.person_roles.map((x: Types.PersonRole, rowIndex: number) => ({...x, rowIndex}))
                                .filter((x: IndexedRow) => x.id === id)
                                .map((x: IndexedRow) => x.rowIndex).concat(-1)[0]
}

type ModalActions = Redux.Action & Actions.UpdateUser & IsActive.StateChangeActionObject & Actions.EditUserAction

const roleEditReducer: UsersModalReducer =
  (state: Types.UserModalState, action: ModalActions): Types.UserModalState => {
  switch (action.type) {
    case IsActive.updateStatusConstant(Constants.UPDATE_PERSON_ROLE):
      const currentRoleIdx = rowIndexById(state, action.change.id)
      if (currentRoleIdx >= 0) {
        return {
          ...state,
          data: {
            ...state.data,
            person_roles: [
              ...state.data.person_roles.slice(0, currentRoleIdx),
              {
                ...state.data.person_roles[currentRoleIdx],
                is_active: action.change.new_value,
              },
              ...state.data.person_roles.slice(currentRoleIdx + 1),
            ],
          },
        }
      } else {
        return {
          ...state,
          data: {
            ...state.data,
            person_roles: [
              ...state.data.person_roles,
              {
                id: action.change.id,
                is_active: action.change.new_value,
                name: action.change.id,
              },
            ],
          },
        }
      }
    default:
      return state
  }
}

// General purpose signature of modal reducers.
type UserModalReducer = Redux.Reducer<Modal.State<Types.ModalData>>

// Listens for the Edit action and populates the modal data with the currently selected data
export const editModalReducer: UserModalReducer =
  (state: Modal.State<Types.ModalData>, action: ModalActions): Modal.State<Types.ModalData> => {
  switch (action.type) {
    case Constants.EDIT_USER:
      return {
        ...state,
        data: {
          email: action.user.email,
          icp_guid: action.user.icp_guid,
          person_roles: action.user.person_roles,
          username: action.user.username,
        },
        isOpen: true,
        modalTitle: 'Edit User',
      }
    case Request.requestConstant(Constants.EDIT_USER):
      return {
        ...state,
        isSubmitting: true,
      }
    case Request.errorConstant(Constants.EDIT_USER):
      return {
        ...state,
        isSubmitting: false,
      }
    case Request.successConstant(Constants.EDIT_USER):
      return {
        ...state,
        isOpen: false,
        isSubmitting: false,
      }
    case Modal.toggleModalConstant(modalReducerEvent.requestEvent):
      return {
        ...state,
        data: Constants.emptyAddUserModalState,
        modalTitle: 'Add New User',
      }
    default:
      return state
  }
}

type IsActiveUserState = IsActive.IsActiveState<Types.User>
const isActiveUpdateReducer =
  (state: IsActiveUserState, action: IsActive.StateChangeActionObject): IsActiveUserState => {
  switch (action.type) {
    case IsActive.updateStatusConstant(Constants.UPDATE_USER): {
      const copy = [
        ...state,
      ]
      const matched = copy.filter((u: Types.User) => u.icp_guid === action.change.props.dataItem.icp_guid)
      matched.forEach((u: any) => u[action.change.key] = action.change.new_value)
      return copy
    }
  }
  return state
}

export const reducer = Redux.combineReducers({
  data: Request.reducerChain([Sortable.stateReducer(reducerEvent), isActiveUpdateReducer]),
  modal: Request.reducerChain([Modal.reducer(modalReducerEvent), roleEditReducer, editModalReducer]),
  view: Sortable.viewReducer(reducerEvent),
})



// WEBPACK FOOTER //
// ./src/pages/users/reducer.ts